import React, { useMemo, useCallback } from 'react';
import { DocumentStatus } from '@alucio/aws-beacon-amplify/src/models';
import { DNAChipProps, DNAChipStatusEnum, DNAConditionalWrapper, DNAText } from '@alucio/lux-ui';
import { DNAChip, DNAPopover } from 'src/components/DNA';

interface DNADocumentStatusChipProps extends DNAChipProps {
  documentStatus: DocumentStatus | keyof typeof DocumentStatus;
  popoverText?: string;
  isScheduled?: boolean;
  isDraft?: boolean;
}

const DOCUMENT_STATUS_DISPLAY_TEXT: Record<DocumentStatus, string> = {
  ARCHIVED: 'ARCHIVED',
  ARCHIVED_WITH_VERSIONS: 'ARCHIVED WITH VERSIONS',
  REVOKED: 'REVOKED',
  REVOKED_WITH_VERSIONS: 'REVOKED WITH VERSIONS',
  NOT_PUBLISHED: 'NOT PUBLISHED',
  PUBLISHED: 'PUBLISHED',
  DELETED: 'DELETED',
  DELETED_WITH_VERSIONS: 'DELETED WITH VERSIONS',
};

// Comprehensive document status configuration
const DOCUMENT_STATUS_CONFIG: Record<DocumentStatus, {
  status: DNAChipStatusEnum;
  icon?: string;
  scheduledIcon?: string;
  draftIcon?: string;
}> = {
  [DocumentStatus.ARCHIVED]: {
    status: DNAChipStatusEnum.secondary,
    icon: undefined,
  },
  [DocumentStatus.ARCHIVED_WITH_VERSIONS]: {
    status: DNAChipStatusEnum.secondary,
    icon: undefined,
  },
  [DocumentStatus.REVOKED]: {
    status: DNAChipStatusEnum.danger,
    icon: undefined,
  },
  [DocumentStatus.REVOKED_WITH_VERSIONS]: {
    status: DNAChipStatusEnum.danger,
    icon: undefined,
  },
  [DocumentStatus.NOT_PUBLISHED]: {
    status: DNAChipStatusEnum.warning,
    icon: undefined,
    scheduledIcon: 'clock',
    draftIcon: 'pencil-simple',
  },
  [DocumentStatus.PUBLISHED]: {
    status: DNAChipStatusEnum.success,
    icon: undefined,
    scheduledIcon: 'clock',
    draftIcon: 'pencil-simple',
  },
  [DocumentStatus.DELETED]: {
    status: DNAChipStatusEnum.danger,
    icon: undefined,
  },
  [DocumentStatus.DELETED_WITH_VERSIONS]: {
    status: DNAChipStatusEnum.danger,
    icon: undefined,
  },
} as const;

/**
 * Custom hook for document status configuration
 */
const useDocumentStatusConfig = (
  documentStatus: DocumentStatus | keyof typeof DocumentStatus,
  isScheduled?: boolean,
  isDraft?: boolean,
) => {
  return useMemo(() => {
    const config = DOCUMENT_STATUS_CONFIG[documentStatus];
    if (!config) {
      return {
        status: DNAChipStatusEnum.secondary,
        icon: undefined,
      };
    }

    // Determine which icon to use based on state
    let icon = config.icon;
    if (isScheduled && config.scheduledIcon) {
      icon = config.scheduledIcon;
    } else if (isDraft && config.draftIcon) {
      icon = config.draftIcon;
    }

    return {
      status: config.status,
      icon,
    };
  }, [documentStatus, isScheduled, isDraft]);
};

/**
 * Beacon-specific DNAChip component for document status
 */
export const DNADocumentStatusChip: React.FC<DNADocumentStatusChipProps> = ({
  documentStatus,
  popoverText,
  isScheduled,
  isDraft,
  style,
  children,
  ...rest
}) => {
  const statusConfig = useDocumentStatusConfig(documentStatus, isScheduled, isDraft);

  const chipContent = useMemo(
    () => children || DOCUMENT_STATUS_DISPLAY_TEXT[documentStatus],
    [children, documentStatus],
  );

  // Memoize the popover wrapper to avoid recreating the function
  const popoverWrapper = useCallback((children: React.ReactNode) => (
    <DNAPopover placement="top">
      <DNAPopover.Anchor>
        {children}
      </DNAPopover.Anchor>
      <DNAPopover.Content offset={2}>
        <DNAText colorMode="dark">
          {popoverText}
        </DNAText>
      </DNAPopover.Content>
    </DNAPopover>
  ), [popoverText]);

  return (
    <DNAConditionalWrapper
      condition={!!popoverText}
      wrapper={popoverWrapper}
    >
      <DNAChip.Beacon
        testID="document-status-badge"
        status={statusConfig.status}
        iconLeft={statusConfig.icon}
        style={style}
        {...rest}
      >
        {chipContent}
      </DNAChip.Beacon>
    </DNAConditionalWrapper>
  );
};

export default DNADocumentStatusChip;
